/**
 * Electrical results view (ENG-14935 prototype UI): renders the NEC Article 220
 * load calc + panel schedules from the raw rollup JSON, plus links to the SVG
 * one-line and CSV exports.
 *
 * The production electrical UI lives in boon-core (ENG-14936); this is the
 * istari prototype's smoke-view so the discipline is clickable end-to-end.
 */
import type { CSSProperties } from "react";
import type {
  Ats,
  EmergencySystem,
  EmergencyBranch,
  GroundingSchedule,
  VoltageDropStudy,
} from "../api/types";
import { groundingScheduleView } from "../mep/grounding";
import { emergencyView } from "../mep/emergency";
import { voltageDropStudyView } from "../mep/voltage_drop";

const sectionTitle: CSSProperties = {
  margin: "1.2rem 0 0.5rem",
  fontSize: "1rem",
};

const card: CSSProperties = {
  background: "#21213a",
  borderRadius: 8,
  padding: "0.8rem 1rem",
  marginBottom: "0.8rem",
};

const table: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.85rem",
};

const th: CSSProperties = {
  textAlign: "left",
  padding: "0.3rem 0.5rem",
  color: "#8e8eb8",
  borderBottom: "1px solid #33335a",
  fontWeight: 600,
};

const td: CSSProperties = {
  padding: "0.3rem 0.5rem",
  borderBottom: "1px solid #2a2a44",
};

interface LoadCalcLine {
  label: string;
  connected_va: number;
  demand_va: number;
  citation?: string | null;
}
interface LoadCalc {
  total_connected_va: number;
  total_demand_va: number;
  demand_amps: number;
  line_items: LoadCalcLine[];
  service_check?: {
    computed_demand_amps: number;
    service_ampacity: number;
    adequate: boolean;
  } | null;
}
interface PanelScheduleRow {
  number: number;
  description: string;
  connected_va: number;
  breaker_amps: number;
  poles: number;
  phase: string;
  wire_awg: string;
}
interface PanelSchedule {
  panel_id: string;
  rows: PanelScheduleRow[];
  footer: {
    phase_a_va: number;
    phase_b_va: number;
    phase_c_va: number;
    total_connected_kva: number;
    total_demand_kva: number;
  };
}

const va = (n: number) => `${Math.round(n).toLocaleString()} VA`;

export interface ElectricalResultsProps {
  loadCalc: unknown;
  panelSchedule: unknown;
  /** Grounding schedule from the raw electrical Output; `null` when the opt-in
   * grounding stage did not run. */
  groundingSchedule?: GroundingSchedule | null;
  /** Emergency system rollup from the raw electrical Output; `null` when the
   * opt-in emergency stage did not run. */
  emergencySystem?: EmergencySystem | null;
  /** Per-element NEC 517.30 branch classification (namespaced element id →
   * branch); empty when the opt-in emergency stage did not run. */
  emergencyBranch?: Record<string, EmergencyBranch>;
  /** Automatic transfer switches; empty when the emergency stage did not run. */
  ats?: Ats[];
  /** Voltage-drop study from the raw electrical Output; `null` when the opt-in
   * voltage-drop stage did not run. */
  voltageDropStudy?: VoltageDropStudy | null;
  systemId: string | null;
}

export function ElectricalResults({
  loadCalc,
  panelSchedule,
  groundingSchedule = null,
  emergencySystem = null,
  emergencyBranch = {},
  ats = [],
  voltageDropStudy = null,
  systemId,
}: ElectricalResultsProps) {
  const lc = loadCalc as LoadCalc | null;
  const panels = (panelSchedule as PanelSchedule[] | null) ?? [];
  const grounding = groundingScheduleView(groundingSchedule);
  const emergency = emergencyView(emergencySystem, emergencyBranch, ats);
  const voltageDrop = voltageDropStudyView(voltageDropStudy);

  return (
    <section data-testid="electrical-results">
      <h2 style={sectionTitle}>NEC Article 220 load calc</h2>
      {lc ? (
        <div style={card}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Line item</th>
                <th style={th}>Connected</th>
                <th style={th}>Demand</th>
                <th style={th}>Citation</th>
              </tr>
            </thead>
            <tbody>
              {lc.line_items.map((l) => (
                <tr key={l.label}>
                  <td style={td}>{l.label}</td>
                  <td style={td}>{va(l.connected_va)}</td>
                  <td style={td}>{va(l.demand_va)}</td>
                  <td style={{ ...td, color: "#8e8eb8", fontSize: "0.78rem" }}>
                    {l.citation ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: "0.6rem", display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
            <span>
              <strong>Total demand:</strong> {va(lc.total_demand_va)}
            </span>
            <span>
              <strong>Service:</strong> {lc.demand_amps.toFixed(1)} A
            </span>
            {lc.service_check && (
              <span
                style={{
                  color: lc.service_check.adequate ? "#98c379" : "#e06c75",
                  fontWeight: 600,
                }}
              >
                {lc.service_check.adequate ? "✓" : "✗"} {lc.service_check.service_ampacity} A
                service {lc.service_check.adequate ? "adequate" : "UNDERSIZED"}
              </span>
            )}
          </div>
        </div>
      ) : (
        <p style={{ color: "#8e8eb8" }}>No load calc yet.</p>
      )}

      <h2 style={sectionTitle}>Panel schedules</h2>
      {panels.length > 0 ? (
        panels.map((panel) => (
          <div key={panel.panel_id} style={card}>
            <div style={{ marginBottom: "0.4rem", fontWeight: 600 }}>
              {panel.panel_id} — {panel.footer.total_connected_kva.toFixed(1)} kVA (A{" "}
              {va(panel.footer.phase_a_va)} · B {va(panel.footer.phase_b_va)} · C{" "}
              {va(panel.footer.phase_c_va)})
            </div>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>Description</th>
                  <th style={th}>VA</th>
                  <th style={th}>Breaker</th>
                  <th style={th}>Phase</th>
                  <th style={th}>Wire</th>
                </tr>
              </thead>
              <tbody>
                {panel.rows.map((r) => (
                  <tr key={r.number}>
                    <td style={td}>{r.number}</td>
                    <td style={td}>{r.description}</td>
                    <td style={td}>{va(r.connected_va)}</td>
                    <td style={td}>
                      {r.breaker_amps} A / {r.poles}P
                    </td>
                    <td style={td}>{r.phase}</td>
                    <td style={td}>#{r.wire_awg}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      ) : (
        <p style={{ color: "#8e8eb8" }}>No panels yet.</p>
      )}

      <h2 style={sectionTitle}>Grounding &amp; bonding schedule (NEC Article 250)</h2>
      {grounding.isEmpty ? (
        <p style={{ color: "#8e8eb8" }} data-testid="grounding-empty">
          No grounding schedule — enable Grounding &amp; bonding in the generate options.
        </p>
      ) : (
        <div style={card} data-testid="grounding-schedule">
          {grounding.conductors.length > 0 && (
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Conductor</th>
                  <th style={th}>Size</th>
                  <th style={th}>From</th>
                  <th style={th}>To</th>
                  <th style={th}>Citation</th>
                </tr>
              </thead>
              <tbody>
                {grounding.conductors.map((r) => (
                  <tr key={`${r.kind}-${r.from_node}-${r.to_node}`}>
                    <td style={td}>{r.label}</td>
                    <td style={td}>#{r.size_awg}</td>
                    <td style={td}>{r.from_node}</td>
                    <td style={td}>{r.to_node}</td>
                    <td style={{ ...td, color: "#8e8eb8", fontSize: "0.78rem" }}>{r.citation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {grounding.electrodes.length > 0 && (
            <table style={{ ...table, marginTop: grounding.conductors.length > 0 ? "0.8rem" : 0 }}>
              <thead>
                <tr>
                  <th style={th}>Electrode</th>
                  <th style={th}>GEC size</th>
                  <th style={th}>Citation</th>
                </tr>
              </thead>
              <tbody>
                {grounding.electrodes.map((r) => (
                  <tr key={`${r.kind}-${r.gec_size_awg}`}>
                    <td style={td}>{r.label}</td>
                    <td style={td}>#{r.gec_size_awg}</td>
                    <td style={{ ...td, color: "#8e8eb8", fontSize: "0.78rem" }}>{r.citation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <h2 style={sectionTitle}>Emergency &amp; standby power (NEC Article 517/700)</h2>
      {emergency.isEmpty ? (
        <p style={{ color: "#8e8eb8" }} data-testid="emergency-empty">
          No emergency system — enable Emergency &amp; standby power in the generate options.
        </p>
      ) : (
        <div style={card} data-testid="emergency-schedule">
          {emergency.source && (
            <div style={{ marginBottom: "0.6rem", fontSize: "0.85rem" }}>
              <strong>Source:</strong> {emergency.source.sourceKind} · {emergency.source.ampacityA}{" "}
              A · {emergency.source.durationMin} min · {emergency.source.systemClass}
            </div>
          )}
          {emergency.schedule.length > 0 && (
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Item</th>
                  <th style={th}>Detail</th>
                  <th style={th}>Size</th>
                  <th style={th}>Transfer</th>
                  <th style={th}>Citation</th>
                </tr>
              </thead>
              <tbody>
                {emergency.schedule.map((r, i) => (
                  <tr key={`${r.kind}-${r.label}-${i}`}>
                    <td style={td}>{r.kind}</td>
                    <td style={td}>{r.label}</td>
                    <td style={td}>{r.size}</td>
                    <td style={td}>{r.transferMode || "—"}</td>
                    <td style={{ ...td, color: "#8e8eb8", fontSize: "0.78rem" }}>{r.citation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {emergency.branches.length > 0 && (
            <>
              <div
                data-testid="emergency-confirm-flag"
                style={{
                  margin: "0.8rem 0 0.4rem",
                  padding: "0.4rem 0.6rem",
                  borderRadius: 6,
                  background: "#3a3320",
                  color: "#e5c07b",
                  fontSize: "0.8rem",
                }}
              >
                ⚠ Heuristic branch classification (NEC 517.30) — a licensed engineer must confirm
                each branch assignment.
              </div>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>Source</th>
                    <th style={th}>Element</th>
                    <th style={th}>Branch</th>
                  </tr>
                </thead>
                <tbody>
                  {emergency.branches.map((r) => (
                    <tr key={r.elementId}>
                      <td style={td}>{r.source}</td>
                      <td style={td}>{r.element}</td>
                      <td style={td}>{r.branchLabel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      <h2 style={sectionTitle}>Voltage-drop analysis (NEC 210.19(A) IN / 215.2(A)(1) IN)</h2>
      {voltageDrop.isEmpty ? (
        <p style={{ color: "#8e8eb8" }} data-testid="voltage-drop-empty">
          No voltage-drop study — enable Voltage-drop analysis in the generate options.
        </p>
      ) : (
        <div style={card} data-testid="voltage-drop-study">
          {voltageDrop.basis && (
            <div style={{ marginBottom: "0.6rem", fontSize: "0.85rem", color: "#8e8eb8" }}>
              <strong>Basis:</strong> {voltageDrop.basis}
            </div>
          )}
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Segment</th>
                <th style={th}>Kind</th>
                <th style={th}>V</th>
                <th style={th}>A</th>
                <th style={th}>Length (ft)</th>
                <th style={th}>Zeff (Ω/kft)</th>
                <th style={th}>PF</th>
                <th style={th}>VD%</th>
                <th style={th}>Threshold</th>
                <th style={th}>Citation</th>
              </tr>
            </thead>
            <tbody>
              {voltageDrop.rows.map((r) => (
                <tr
                  key={`${r.kind}-${r.id}`}
                  data-testid={`voltage-drop-row-${r.verdict}`}
                  style={
                    r.verdict === "exceeded"
                      ? { background: "#3a2020", color: "#e88" }
                      : undefined
                  }
                >
                  <td style={td}>{r.id}</td>
                  <td style={td}>{r.kind}</td>
                  <td style={td}>{r.voltage}</td>
                  <td style={td}>{r.amps}</td>
                  <td style={td}>{r.length_ft}</td>
                  <td style={td}>{r.zeffText}</td>
                  <td style={td}>{r.power_factor}</td>
                  <td style={{ ...td, fontWeight: r.verdict === "exceeded" ? 700 : 400 }}>
                    {r.percentText}
                    {r.verdict === "exceeded" ? " ⚠" : ""}
                  </td>
                  <td style={td}>
                    {r.verdict === "informational"
                      ? `${r.thresholdText} (comb. ceiling)`
                      : r.thresholdText}
                  </td>
                  <td style={{ ...td, color: "#8e8eb8", fontSize: "0.78rem" }}>{r.citation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {systemId && (
        <p style={{ fontSize: "0.85rem" }}>
          <strong>Exports:</strong>{" "}
          <a href={`/api/mep-systems/${systemId}/one-line`} target="_blank" rel="noreferrer">
            One-line (SVG)
          </a>{" "}
          ·{" "}
          <a href={`/api/mep-systems/${systemId}/load-calc`} target="_blank" rel="noreferrer">
            Load calc (JSON)
          </a>{" "}
          ·{" "}
          <a
            href={`/api/mep-systems/${systemId}/panel-schedule.csv`}
            target="_blank"
            rel="noreferrer"
          >
            Panel schedule (CSV)
          </a>{" "}
          ·{" "}
          <a href={`/api/mep-systems/${systemId}/boq.csv`} target="_blank" rel="noreferrer">
            BoQ (CSV)
          </a>
        </p>
      )}

      {!emergency.isEmpty && (
        <p
          data-testid="emergency-one-line-legend"
          style={{ color: "#8e8eb8", fontSize: "0.78rem", marginTop: "0.4rem" }}
        >
          One-line legend: the <strong>Emergency Source</strong> node (alternate source, NEC
          700/517) feeds through <strong>ATS</strong> transfer nodes to one branch panel per
          essential- electrical-system branch (Life-Safety / Critical / Equipment).
        </p>
      )}
    </section>
  );
}
